import React from 'react'
import { Plus, Folder } from 'lucide-react'
import { Button, Chip } from 'components/ui'
import keys from 'constants/keys'
import useLocaleText from 'components/useLocaleText'
import { useCreationText } from '../../hooks/useCreationText'
import type { useSubtaskMenu } from '../../hooks/useSubtaskMenu'
import { PreviewTitle } from '../SubtaskPreviewCard/PreviewTitle'
import { subtaskTypeKeys } from '../../model/subtaskTypes'
import styles from './styles.module.css'

interface ITaskLivePreviewProps {
  groups?: { name: string; subtasks: { title: string; type: number }[] }[]
  activeCategory?: string
  showOptions?: boolean
  title: string
  buttons: ReturnType<typeof useSubtaskMenu>['buttons']
  disabled: boolean
  onAdd: (type: number) => void
}

export function TaskLivePreview({
  title,
  buttons,
  disabled,
  onAdd,
  groups,
  activeCategory,
  showOptions = true,
}: ITaskLivePreviewProps) {
  const t = useCreationText()
  const noCategories = useLocaleText({
    text: { key: 'task_creation_no_categories', fallback: { en: 'No categories yet', he: 'אין קטגוריות עדיין' } },
  }).text
  const ready = !!title.trim()
  const options = buttons.filter(
    ({ type }) => type !== keys.SUBTASK_TYPES.DESCRIPTION && type !== keys.SUBTASK_TYPES.LINK_SUBTASK
  )
  return (
    <div className={styles.preview} data-ready={ready} data-grouped={!!groups}>
      <figure className={styles.figure} aria-label={t.livePreview}>
        <figcaption>{t.livePreview}</figcaption>
        <div className={styles.phone}>
          <div className={styles.notch} aria-hidden="true" />
          <div className={styles.screen}>
            <header>
              <strong>{t.myTasks}</strong>
              <span aria-hidden="true" />
            </header>
            <div className={styles.task}>
              <Chip label={<>{t.newTask}</>} color="#477cec" size="sm" />
              <strong className={styles.title} data-placeholder={!ready}>
                {ready ? title : t.previewName}
              </strong>
              {groups && !groups.length ? <p>{noCategories}</p> : ready && !groups && <p>{t.noSubtasks}</p>}
              {!!groups?.length && (
                <div className={styles.groups}>
                  {groups.map((group) => (
                    <section key={group.name} data-active={group.name === activeCategory}>
                      <div className={styles.groupHeader}>
                        <Folder size={13} />
                        <strong dir="auto">{group.name}</strong>
                        <span>0/{group.subtasks.length}</span>
                      </div>
                      {group.subtasks.length ? (
                        group.subtasks.map((subtask, index) => {
                          const Icon = buttons.find((button) => button.type === subtask.type)?.Component
                          return (
                            <p key={index} className={styles.groupItem}>
                              {Icon && <Icon width="24px" height="24px" />}
                              <PreviewTitle value={subtask.title} fallback={t.untitled} />
                            </p>
                          )
                        })
                      ) : (
                        <p>{t.noSubtasks}</p>
                      )}
                    </section>
                  ))}
                </div>
              )}
              <div hidden={!!groups} className={styles.skeleton} aria-hidden="true">
                {[0, 1, 2].map((item) => (
                  <div key={item}>
                    <i />
                    <span />
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.ghost} hidden={!!groups} aria-hidden="true">
              <span />
              <span />
            </div>
          </div>
          <div className={styles.home} aria-hidden="true" />
        </div>
      </figure>
      {ready && showOptions ? (
        <section className={styles.options} aria-label={t.chooseFirst}>
          <h3>{t.chooseFirst}</h3>
          <div className={styles.optionList}>
            {options.map(({ type, Component }) => {
              const label = t[subtaskTypeKeys[type]]
              return (
                <div key={type} className={styles.option}>
                  <Component width="40px" height="40px" />
                  <strong>{label}</strong>
                  <Button
                    type="button"
                    variant="secondary"
                    isDisabled={disabled}
                    onClick={() => onAdd(type)}
                    aria-label={`${t.addType} ${label}`}
                    startIcon={<Plus size={14} />}
                  >
                    {t.addType}
                  </Button>
                </div>
              )
            })}
          </div>
          <p>{t.firstEditorHint}</p>
        </section>
      ) : (
        <div className={styles.hint}>
          <h3>{t.teamPreview}</h3>
          {!ready && !groups && <p>{t.previewHint}</p>}
        </div>
      )}
    </div>
  )
}
